import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  TURNOS_INSTALACAO, partesEmSaoPaulo, rotuloTurno, turnoDaAgenda, valorDoTurno, type TurnoInstalacao,
} from '@/lib/turnoInstalacao';

interface Props {
  titulo: string;
  descricao: string;
  dataAtual?: string | null;
  pedeMotivo?: boolean;
  confirmar: string;
  salvando: boolean;
  onSalvar: (dados: { dataIso: string; motivo: string }) => void;
  children: ReactNode;
  aberto: boolean;
  onAberto: (aberto: boolean) => void;
}

export function AgendaInstalacaoPopover({
  titulo, descricao, dataAtual, pedeMotivo, confirmar, salvando, onSalvar, children, aberto, onAberto,
}: Props) {
  const [dia, setDia] = useState(partesEmSaoPaulo(dataAtual)?.data ?? '');
  const [turno, setTurno] = useState<TurnoInstalacao | null>(turnoDaAgenda(dataAtual));
  const [motivo, setMotivo] = useState('');

  const abrir = (o: boolean) => {
    if (o) {
      setDia(partesEmSaoPaulo(dataAtual)?.data ?? '');
      setTurno(turnoDaAgenda(dataAtual));
      setMotivo('');
    }
    onAberto(o);
  };

  const falta = !/^\d{4}-\d{2}-\d{2}$/.test(dia)
    ? 'Escolha a data da instalação.'
    : !turno
      ? 'Escolha o turno para salvar a agenda.'
      : pedeMotivo && motivo.trim().length < 5
        ? 'Escreva o motivo do reagendamento.'
        : null;

  return (
    <Popover open={aberto} onOpenChange={abrir}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 space-y-2.5 p-3" align="end">
        <div>
          <p className="text-xs font-semibold">{titulo}</p>
          <p className="text-[10.5px] text-muted-foreground">{descricao}</p>
        </div>
        <Label className="text-[11px] text-muted-foreground">Data e turno da instalação</Label>
        <Input type="date" value={dia} onChange={(e) => setDia(e.target.value)} className="h-8 text-xs" />
        <div className="flex h-8 items-stretch gap-0.5 rounded-md bg-[#EDE8E0] p-0.5 dark:bg-[#161010]" role="radiogroup" aria-label="Turno da instalação">
          {TURNOS_INSTALACAO.map((t) => {
            const ativo = turno?.inicio === t.inicio;
            return (
              <button
                key={t.inicio}
                type="button"
                role="radio"
                aria-checked={ativo}
                onClick={() => setTurno(t)}
                className={cn(
                  'min-w-0 flex-1 whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-semibold tabular-nums transition-colors',
                  ativo
                    ? 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]'
                    : 'text-[#6B5A4D] hover:text-[#5B0A0D] dark:text-[#B5A8A8] dark:hover:text-white',
                )}
              >
                {rotuloTurno(t)}
              </button>
            );
          })}
        </div>
        {pedeMotivo && (
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por que a instalação mudou de data"
            className="min-h-[56px] text-xs"
          />
        )}
        {falta && <p className="text-[10.5px] text-muted-foreground">{falta}</p>}
        <Button
          size="sm"
          className="h-8 w-full"
          disabled={!!falta || salvando}
          onClick={() => turno && onSalvar({ dataIso: valorDoTurno(dia, turno), motivo: motivo.trim() })}
        >
          {salvando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirmar}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
